import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import withRouter from 'react-router/lib/withRouter';
import { routerShape } from 'react-router/lib/PropTypes';

import classnames from 'classnames';
import Col from 'react-bootstrap/lib/Col';

import t from '../../lib/I18n';
import ModalActions from '../../actions/ModalActions';
import config from '../../config';
import trainTime from '../../lib/TrainTime';
import { SeasonStages, AccountPages, PointStages } from '../../constants/AppConstants';
import urls from '../../urls';
import Smartcard from '../../records/customer/Smartcard';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import UserStore from '../../stores/UserStore';
import connectToStores from '../../helpers/connectToStores';
import Assets from '../../helpers/Assets';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import BaseModal, { baseModalScrollTo } from '../general/BaseModal';
import FeatureFlag from '../general/FeatureFlag';
import Icon from '../general/Icon';
import TickIcon from '../icons/TickIcon';
import Link from '../general/Link';
import RegisterHello from './RegisterHello';
import Select from '../form/Select';
import ProgressBar from '../general/ProgressBar';


const DISABLED_TIME_SECONDS = 10;

const successOptions = [
    {
        option: 'account.register.findDayTicketButtonText',
        location: PointStages.SEARCH,
        extra: null,
        shouldInclude: props =>
            !!props.smartcard &&
            props.enableDayTicketPreload &&
            props.smartcard.isAwaitingProduct(),
    },
    {
        option: 'account.register.findSeasonTicketButtonText',
        location: SeasonStages.SEARCH,
        extra: null,
        shouldInclude: props =>
            !!props.smartcard && props.productPreloadEnabled && props.smartcard.isAwaitingProduct(),
    },
    {
        option: 'account.register.transferTicketButtonText',
        location: AccountPages.SMARTCARD_TRANSFER,
        extra: null,
        shouldInclude: props => props.smartcardPaperTransfersEnabled,
    },
    {
        option: 'account.register.keygoButtonText',
        location: AccountPages.SMARTCARD_KEYGO,
        extra: null,
        shouldInclude: props => props.enableKeyGoRegistrationAtSignup,
    },
    {
        option: 'account.register.ocdrButtonText',
        extra: null,
        location: AccountPages.ONE_CLICK_DELAY_REPAY,
        shouldInclude: props => props.oneClickDelayRepayEnabled,
    },
];

export const RegisterSuccessModal = props => {
    let timeoutID = null;
    const [state, setComponentState] = useState({
        disabledWaitTime: DISABLED_TIME_SECONDS,
        timeCompleted: 0,
        selectedOption: null,
        isSubscribeModalOpen: false,
    });

    useEffect(() => {
        const { smartcard } = props;
        const isSmartcardAwaitingProduct = smartcard && smartcard.isAwaitingProduct();

        if (isSmartcardAwaitingProduct) {
            startCountdown();
        }
        return () => {
            clearTimeout(timeoutID);

            // Hacky! Scroll to the top of the app so that user can see the logged-in version of the header
            setTimeout(() => {
                document.body.scrollTop = document.documentElement.scrollTop = 0;
            }, 500);
        };
        // eslint-disable-next-line
    }, []);

    const startCountdown = () => {
        let timeleft = state.disabledWaitTime;
        const countdown = () => {
            timeleft = timeleft - 1;
            setComponentState({
                ...state,
                disabledWaitTime: timeleft,
                timeCompleted: DISABLED_TIME_SECONDS - (timeleft + 1),
            });
            if (timeleft) {
                timeoutID = setTimeout(countdown, props.disableSuccessDropdown ? 0 : 1000);
            } else {
                const nextCtaScrollTop = (
                    document.querySelector('.modal button') ||
                    document.querySelector('.modal select')
                ).getBoundingClientRect().top;
                baseModalScrollTo({ top: nextCtaScrollTop });
            }
        };

        countdown();
    };

    const handleCancel = () => {
        const { onSuccessClose } = props;
        if (onSuccessClose) {
            onSuccessClose();
        }
        ModalActions.close();
    };

    const handleSelect = event => {
        const selectedVal = event.target.value;
        const selectedOption = getSuccessActions().find(({ location }) => location === selectedVal);
        if (selectedOption) {
            setComponentState({ ...state, selectedOption });
        }
    };

    const handleContinue = () => {
        const { selectedOption } = state;
        const { onOptInADR } = props;

        if (selectedOption.location === AccountPages.ONE_CLICK_DELAY_REPAY) {
            onOptInADR();
            handleCancel();
        } else {
            pushRoute(selectedOption.location, null, selectedOption.extra);
        }
    };

    const pushRoute = (page, param, other) => {
        props.router.push(urls.location(page, param, other));
        ModalActions.close();
    };

    const getSuccessActions = () => successOptions.filter(opt => opt.shouldInclude(props));

    const onContinue = () => {
        const { onOptInADR } = props;
        if (smartcardOnly) {
            handleCancel();
        } else {
            onOptInADR();
            // handleCancel();
        }
    };

    const {
        smartcard,
        smartcardOnly,
        productPreloadEnabled,
        enableKeyGoRegistrationAtSignup,
        disableSuccessDropdown,
        user,
    } = props;
    const { disabledWaitTime, selectedOption, timeCompleted } = state;
    const isSmartcardAwaitingProduct =
        !!smartcard && productPreloadEnabled && smartcard.isAwaitingProduct();

    let modalProps = {
        testId: 'register-success-modal',
        disableBlur: !disableSuccessDropdown,
        disableEscape: !disableSuccessDropdown,
        titleText: smartcardOnly
            ? 'account.register.smartcardOnlySuccess'
            : 'account.register.success',
        continueText: 'auto-dr.account.signup',
        closeText: 'button.text.close',
        onContinue: onContinue,
        onClose: handleCancel,
        hideContinue: smartcard ? true : !FeatureFlagStore.get('enableOneClickDelayRepay'),
        className: 'modal--sm register-success-modal',
        header: false,
        showCloseFooterButton: true,
    };

    if (isSmartcardAwaitingProduct) {
        Object.assign(modalProps, {
            disableClose: !!disabledWaitTime,
            disableContinue: !!disabledWaitTime,
        });
    }

    const preloadTimeLimit = (
        <strong>{trainTime.duration(config.get('smartcardPreloadTimeLimit')).humanize()}</strong>
    );
    const awaitSmartcardProductDays = (
        <strong>
            {trainTime()
                .addWorkingDays(config.get('minAwaitingSmartcardProductDays'))
                .format('DD-MMM-YY')}
        </strong>
    );

    return (
        <React.Fragment>
            <BaseModal {...modalProps}>
                <div className="vertical-align-parent full-height">
                    <div
                        className={classnames('confirmation text-center', {
                            'vertical-align': !isSmartcardAwaitingProduct,
                        })}
                    >
                        {isSmartcardAwaitingProduct && (
                            <ProgressBar
                                completed={timeCompleted}
                                total={DISABLED_TIME_SECONDS - 2}
                            />
                        )}

                        <TickIcon />

                        <RegisterHello name={user.firstNames} />

                        {smartcard ? (
                            <div>
                                <p>
                                    {t('account.register.smartcardApplicationReceived')}
                                    <Link
                                        disabled={!!disabledWaitTime}
                                        onClick={handleCancel}
                                        to={AccountPages.SMARTCARD_APPLY}
                                    >
                                        {t('account.register.viewAccount')}
                                    </Link>
                                    .&nbsp;
                                </p>
                            </div>
                        ) : (
                            <FeatureFlag name="enableOneClickDelayRepay">
                                <div className="margin-top--md margin-bottom--sm text-left">
                                    <h2 className="auto-dr__header auto-dr__header--post-registration">
                                        <img
                                            src={Assets.assetUrl(
                                                'images/1CDR-arrows.png',
                                            )}
                                            alt={t('auto-dr.account.arrows')}
                                            className="titleImg"
                                        />
                                        <span className="adr-title">{t('auto-dr.subscribe.heading')}</span>
                                    </h2>
                                    <FormattedHTMLMessage message='auto-dr.postRegistrationSignUp.info' splitParagraphs />
                                </div>
                            </FeatureFlag>
                        )}

                        {isSmartcardAwaitingProduct && (
                            <div className="margin-top">
                                <p>
                                    <FormattedHTMLMessage
                                        message={
                                            disableSuccessDropdown
                                                ? 'account.register.smartcardActivation'
                                                : enableKeyGoRegistrationAtSignup
                                                ? 'account.register.keyGoPreload'
                                                : 'account.register.smartcardPreload'
                                        }
                                        buyTicketsLink={
                                            <Link
                                                to={SeasonStages.SEARCH}
                                                query={{ smartcard: true }}
                                                absolute
                                            >
                                                {t('account.register.smartcardPreload.new')}
                                            </Link>
                                        }
                                        paperTransferLink={
                                            <Link to={AccountPages.SMARTCARD_TRANSFER} absolute>
                                                {t('account.register.smartcardPreload.existing')}
                                            </Link>
                                        }
                                        preloadTimeLimit={preloadTimeLimit}
                                        awaitSmartcardProductDays={awaitSmartcardProductDays}
                                    />
                                </p>
                                {!disableSuccessDropdown && (
                                    <Col xs={12} sm={12} className="margin-top--sm">
                                        <div className="register-modal__success__dropdown-wrapper">
                                            <Col xs={9}>
                                                <span className="register-modal__success-dropdown select-container">
                                                    <label
                                                        className="sr-only"
                                                        htmlFor="option__selector"
                                                    >
                                                        {t('account.register.whatsNext')}
                                                    </label>
                                                    <Select
                                                        id="option__selector"
                                                        type="select"
                                                        onChange={handleSelect}
                                                        disabled={!!disabledWaitTime}
                                                    >
                                                        <option
                                                            value={-1}
                                                            key={-1}
                                                            disabled
                                                            selected
                                                        >
                                                            {t('account.register.whatsNext')}
                                                        </option>
                                                        {getSuccessActions().map(
                                                            ({ option, location }) => (
                                                                <option
                                                                    value={location}
                                                                    key={location}
                                                                >
                                                                    {t(option)}
                                                                </option>
                                                            ),
                                                        )}
                                                    </Select>
                                                </span>
                                            </Col>
                                            <Col xs={3}>
                                                <button
                                                    className={classnames(
                                                        'register-modal__success-button action-btn action-btn--primary modal__footer__button modal__footer__button--primary',
                                                        {
                                                            'modal__footer__button--inactive': !selectedOption,
                                                        },
                                                    )}
                                                    onClick={handleContinue}
                                                    disabled={!selectedOption}
                                                >
                                                    <Icon
                                                        icon="arrowrightslim"
                                                        className="modal__footer__button__arrow"
                                                    />
                                                    <span className="modal__footer__button__text">
                                                        {t(
                                                            'account.register.partialSuccess.button',
                                                        )}
                                                    </span>
                                                </button>
                                            </Col>
                                        </div>
                                    </Col>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </BaseModal>
        </React.Fragment>
    );
};

RegisterSuccessModal.propTypes = {
    router: routerShape,
    smartcard: PropTypes.instanceOf(Smartcard),
    smartcardOnly: PropTypes.bool,
    productPreloadEnabled: PropTypes.bool,
    smartcardPaperTransfersEnabled: PropTypes.bool,
    enableKeyGoRegistrationAtSignup: PropTypes.bool,
    disableSuccessDropdown: PropTypes.bool,
    enableDayTicketPreload: PropTypes.bool,
    onSuccessClose: PropTypes.func,
    user: PropTypes.obj,
    onOptInADR: PropTypes.func,
};

RegisterSuccessModal.defaultProps = {
    smartcard: null,
    smartcardOnly: false,
    disableSuccessDropdown: false,
    enableDayTicketPreload: false,
};

function getStateFromStores() {
    return {
        productPreloadEnabled: FeatureFlagStore.get('enableProductPreload'),
        smartcardPaperTransfersEnabled: FeatureFlagStore.get('enableSmartcardPaperTransfers'),
        enableKeyGoRegistrationAtSignup: FeatureFlagStore.get('enableKeyGoRegistrationAtSignup'),
        enableDayTicketPreload: FeatureFlagStore.get('enableDayTicketPreload'),
        user: UserStore.getCurrent(),
    };
}

export default connectToStores(
    [FeatureFlagStore, UserStore],
    getStateFromStores,
)(withRouter(RegisterSuccessModal));
